import { type Href, useRouter } from 'expo-router'
import { autoLocationForJobAddress } from '@/hooks/jobs'
import { useCurrentOrg } from '@/hooks/useCurrentOrg'
import { findOrCreateAddress } from '@/lib/address'
import { DEFAULT_LOCATION_NAME, uploadLocationThumbnail } from '@/lib/locations'
import { performMutations, useMutation } from '@/lib/mutations'
import { notify } from '@/lib/notice'
import { newRecordId, pb, useStore } from '@/lib/pocketbase'
import { isAddressBlank, type JobFormData } from '@/types/jobs'
import type { Addresses } from '@/types/pbSchema'

export function useUpdateJob(orgSlug: string) {
    const router = useRouter()
    const { org: currentOrg } = useCurrentOrg()

    const [jobsCollection, locationsCollection] = useStore('jobs', 'locations')

    if (!currentOrg) throw new Error('No current organization found')

    return useMutation({
        mutationFn: async (data: JobFormData) => {
            const { address } = data.expand
            const { customer } = data
            if (!data.id || !customer?.id) {
                throw new Error('Missing expanded address or customer data')
            }

            let locationData = null

            if (!isAddressBlank(address)) {
                locationData = await autoLocationForJobAddress(address as Addresses)
            }

            const existingLocations = await pb.collection('locations').getFullList({
                filter: `job = "${data.id}"`,
            })
            const hasExistingLocation = existingLocations.length > 0

            let newLocationId: string | null = null

            const result = await performMutations(function* () {
                const { transaction: addressTransaction, addressId } = findOrCreateAddress(
                    address,
                    currentOrg.id
                )

                if (addressTransaction) {
                    yield addressTransaction
                }

                if (locationData && data.id && addressId && !hasExistingLocation) {
                    newLocationId = newRecordId()
                    yield locationsCollection.insert({
                        id: newLocationId,
                        inactive: false,
                        name: DEFAULT_LOCATION_NAME,
                        org: currentOrg.id,
                        job: data.id,
                        tags: [],
                        ...locationData,
                    })
                }

                yield jobsCollection.update(data.id, draft => {
                    Object.assign(draft, {
                        ...data,
                        customer: customer.id,
                        address: addressId,
                        started: data.started ? new Date(data.started).toISOString() : '',
                        completed: data.completed ? new Date(data.completed).toISOString() : '',
                        tags: data.tags ?? [],
                    })
                })
            })

            if (newLocationId && locationData) {
                uploadLocationThumbnail({
                    id: newLocationId,
                    name: data.name,
                    geometry: locationData.geometry,
                })
            }

            return result
        },
        onSuccess: (_result, data) => {
            notify.success('Job updated successfully')
            router.replace(`/${orgSlug}/settings/jobs/${data.identifier}` as Href)
        },
    })
}
